import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { CREDIT_TRANSACTION_TYPES } from '@/modules/Customer/components/Detail/Credits/constants'
import {
    createCustomerCredit,
    getCustomerCredits,
} from '@/modules/Customer/components/Detail/Credits/services'
import { CreateCustomerCredit } from '@/modules/Customer/components/Detail/Credits/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaCreateCreditTransactionModal } from './schema'

export interface CreateCreditModalProps extends ModalProps {
    customerID: number
    creditID: number
}

const CreateCreditModal = ({
    onClose,
    open,
    customerID,
    creditID,
}: CreateCreditModalProps) => {
    const { t } = useTranslation()

    const { data: credits, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_CREDITS, customerID],
        queryFn: () => getCustomerCredits({ customerID }),
    })

    const formControl = useForm<CreateCustomerCredit>({
        defaultValues: {
            credit_id: creditID || -1,
            credit_transaction_amount: 0,
            credit_transaction_note: '',
            credit_transaction_type: CREDIT_TRANSACTION_TYPES.Deposit,
        },
        resolver: yupResolver(schemaCreateCreditTransactionModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createCustomerCredit },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('add-new-subcontract-success'),
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleCreate = (values: CreateCustomerCredit) => {
        mutateAsync({
            customerID,
            data: values,
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Dialog
            title={t('credit.transaction.create')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('credit.transaction.create'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'credit_id',
                            type: 'select',
                            label: t('customers.credits.transactions.choose'),
                            placeholder: t(
                                'customers.credits.transactions.choose'
                            ),
                            options: (!credits?.results
                                ? []
                                : credits?.results?.map((credit) => {
                                      return {
                                          value: credit?.credit_id,
                                          label: credit?.credit_name,
                                      }
                                  }) || []
                            ).concat([
                                { value: -1, label: t('create.credits.new') },
                            ]),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'credit_transaction_type',
                            type: 'select',
                            label: t('credit_transaction_type'),
                            placeholder: t('credit_transaction_type'),
                            options: Object.entries(CREDIT_TRANSACTION_TYPES)
                                ?.filter(([_key, value]) =>
                                    [
                                        CREDIT_TRANSACTION_TYPES.Deposit,
                                        CREDIT_TRANSACTION_TYPES.Refund,
                                    ].includes(value)
                                )
                                ?.map(([_key, value]) => ({
                                    value,
                                    label: t(value),
                                })),
                            disabled: formControl.watch('credit_id') <= 0,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'credit_transaction_amount',
                            type: 'number',
                            label: t('credit_transaction_amount'),
                            placeholder: t('credit_transaction_amount'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'credit_transaction_note',
                            type: 'textarea',
                            label: t('note'),
                            placeholder: t('note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCreditModal
